var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { makeObservable, observable, action, runInAction } from 'mobx';
import { defaultsDeep, merge } from 'lodash';
import { getAxios } from '../../backend';
import { API_PATH } from '../../constants';
import partnerThemePresetsJSON from '../../assets/themePresetsPartner/presets.json';
const partnerThemePresets = Object.entries(partnerThemePresetsJSON).reduce((acc, [key, value], i) => {
    acc[key] = Object.assign(Object.assign({}, value), { icon: require(`../../assets/themePresetsPartner/icons/${key.toLowerCase()}.png`), iconSmall: require(`../../assets/themePresetsPartner/icons/${key.toLowerCase()}-small.png`) });
    return acc;
}, {});
export const generalSettingsDefault = {
    user: {
        heartbeatInterval: 5 * 60 * 1000,
        awayTimeout: 45 * 60 * 1000,
        logoutCountdownStart: 120 * 60 * 1000,
        logoutCountdownLength: 10 * 60 * 1000,
    },
    inbox: {},
    partner: {
        themes: partnerThemePresets,
    },
};
class SettingsStore {
    constructor(rootStore) {
        Object.defineProperty(this, "rootStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "generalSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: Object.assign({}, generalSettingsDefault)
        });
        Object.defineProperty(this, "generalSettingsLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "scheduledDeletionSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "scheduledDeletionLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "autoResolveSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "autoResolveLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "loadGeneralSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => __awaiter(this, void 0, void 0, function* () {
                runInAction(() => (this.generalSettingsLoading = true));
                yield getAxios()
                    .then((axios) => {
                    return axios.get(API_PATH.SETTINGS);
                })
                    .then(({ data: settings }) => {
                    this.mergeSettings(settings);
                })
                    .catch((error) => {
                    console.error(error);
                })
                    .finally(() => {
                    runInAction(() => (this.generalSettingsLoading = false));
                });
            })
        });
        Object.defineProperty(this, "mergeSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (settings) => {
                Object.assign(this.generalSettings, defaultsDeep(settings, this.generalSettings, generalSettingsDefault));
                console.debug('General settings updated', JSON.stringify(this.generalSettings));
            }
        });
        Object.defineProperty(this, "resetSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                /* remember partnerTheme since it is needed after logout */
                this.generalSettings = merge({}, generalSettingsDefault, {
                    partner: {
                        selectedTheme: this.generalSettings.partner.selectedTheme,
                    },
                });
                console.debug('General settings reset');
            }
        });
        Object.defineProperty(this, "getScheduledDeletionSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (...args_1) => __awaiter(this, [...args_1], void 0, function* (forceRefresh = false) {
                // Return cached settings if available and not forcing refresh
                if (this.scheduledDeletionSettings && !forceRefresh) {
                    return this.scheduledDeletionSettings;
                }
                this.scheduledDeletionLoading = true;
                return getAxios()
                    .then((axios) => {
                    return axios.get(`${API_PATH.SETTINGS}/scheduled-deletion/messages`);
                })
                    .then(({ data }) => {
                    this.scheduledDeletionSettings = data;
                    this.scheduledDeletionLoading = false;
                    return data;
                })
                    .catch((error) => {
                    var _a, _b;
                    console.error('Error fetching scheduled deletion settings', error);
                    this.scheduledDeletionLoading = false;
                    // Empty settings throws error, returning empty object instead
                    if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 500 || ((_b = error.response) === null || _b === void 0 ? void 0 : _b.status) === 404) {
                        this.scheduledDeletionSettings = {};
                        return this.scheduledDeletionSettings;
                    }
                    throw error;
                });
            })
        });
        Object.defineProperty(this, "updateScheduledDeletionSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (settings) => __awaiter(this, void 0, void 0, function* () {
                return getAxios()
                    .then((axios) => {
                    return axios.patch(`${API_PATH.SETTINGS}/scheduled-deletion/messages`, settings);
                })
                    .then(({ data }) => {
                    // Update the cached settings with the response
                    this.scheduledDeletionSettings = data;
                    return data;
                })
                    .catch((error) => {
                    console.error('Error updating scheduled deletion settings', error);
                    throw error;
                });
            })
        });
        Object.defineProperty(this, "getAutoResolveSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (...args_1) => __awaiter(this, [...args_1], void 0, function* (forceRefresh = false) {
                if (this.autoResolveSettings && !forceRefresh) {
                    return this.autoResolveSettings;
                }
                this.autoResolveLoading = true;
                return getAxios()
                    .then((axios) => {
                    return axios.get(`${API_PATH.SYSTEM}/automations/chats/auto-resolve`);
                })
                    .then(({ data }) => {
                    runInAction(() => {
                        this.autoResolveSettings = data;
                        this.autoResolveLoading = false;
                    });
                    return data;
                })
                    .catch((error) => {
                    console.error('Error fetching auto-resolve settings', error);
                    runInAction(() => {
                        this.autoResolveLoading = false;
                    });
                    throw error;
                });
            })
        });
        Object.defineProperty(this, "updateAutoResolveSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (settings) => __awaiter(this, void 0, void 0, function* () {
                return getAxios()
                    .then((axios) => {
                    return axios.patch(`${API_PATH.SYSTEM}/automations/chats/auto-resolve`, settings);
                })
                    .then(({ data }) => {
                    runInAction(() => {
                        this.autoResolveSettings = settings;
                    });
                    return data;
                })
                    .catch((error) => {
                    console.error('Error updating auto-resolve settings', error);
                    throw error;
                });
            })
        });
        makeObservable(this);
        this.rootStore = rootStore;
    }
}
__decorate([
    observable
], SettingsStore.prototype, "generalSettings", void 0);
__decorate([
    observable
], SettingsStore.prototype, "generalSettingsLoading", void 0);
__decorate([
    observable
], SettingsStore.prototype, "scheduledDeletionSettings", void 0);
__decorate([
    observable
], SettingsStore.prototype, "scheduledDeletionLoading", void 0);
__decorate([
    observable
], SettingsStore.prototype, "autoResolveSettings", void 0);
__decorate([
    observable
], SettingsStore.prototype, "autoResolveLoading", void 0);
__decorate([
    action
], SettingsStore.prototype, "loadGeneralSettings", void 0);
__decorate([
    action
], SettingsStore.prototype, "mergeSettings", void 0);
__decorate([
    action
], SettingsStore.prototype, "resetSettings", void 0);
__decorate([
    action
], SettingsStore.prototype, "getScheduledDeletionSettings", void 0);
__decorate([
    action
], SettingsStore.prototype, "updateScheduledDeletionSettings", void 0);
__decorate([
    action
], SettingsStore.prototype, "getAutoResolveSettings", void 0);
__decorate([
    action
], SettingsStore.prototype, "updateAutoResolveSettings", void 0);
export default SettingsStore;
